import { memo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { MessageSquareQuote, Paperclip, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { uniqueAttachments, type ChatAttachment } from '@hub/contracts'
import { EmailEnvelope } from './email-envelope'
import { ImageLightbox } from './image-lightbox'
import type { ChatMessage } from '@/lib/collab/use-chat-collab'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { field, ghostButton, mono } from '@/components/assistant-ui/elements/surfaces'
import { cn } from '@/lib/utils'
import { splitQuotedSelections } from '@/lib/chat/selections'
import { formatFileSize, formatTime } from './time'

function initials(name: string | undefined): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  return name.slice(0, 2).toUpperCase()
}

function AttachmentChip({ att, projectId }: { att: ChatAttachment; projectId: string }) {
  const { t } = useTranslation('chat')
  const isImage = att.contentType?.startsWith('image/') ?? false
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const inner = (
    <>
      <Paperclip className="size-3.5 shrink-0 text-blue-500" />
      <span className="max-w-48 truncate text-xs font-medium">{att.filename}</span>
      <span className={cn(mono, 'text-foreground/40')}>
        {att.documentId
          ? t('message.attachmentDocument')
          : att.size > 0
            ? formatFileSize(att.size)
            : t('message.attachmentFile')}
      </span>
    </>
  )
  const chip = cn(field, 'flex items-center gap-2 rounded-[14px] px-2.5 py-1.5 transition-colors')

  if (isImage && att.url && !att.documentId) {
    return (
      <>
        <button
          type="button"
          onClick={() => setLightboxOpen(true)}
          aria-label={t('lightbox.open', { name: att.filename })}
          className="bg-card block max-w-xs cursor-zoom-in overflow-hidden rounded-xl border text-left shadow-sm"
        >
          <img src={att.url} alt={att.filename} className="block max-h-64 w-auto" loading="lazy" />
          <div className="flex items-center gap-2 border-t px-3 py-2">{inner}</div>
        </button>
        <ImageLightbox
          image={{ url: att.url, filename: att.filename, size: att.size }}
          open={lightboxOpen}
          onOpenChange={setLightboxOpen}
        />
      </>
    )
  }
  if (att.documentId) {
    return (
      <Link
        to="/$projectId/write/$docId"
        params={{ projectId, docId: att.documentId }}
        className={cn(chip, 'hover:bg-foreground/[0.08]')}
      >
        {inner}
      </Link>
    )
  }
  if (att.url) {
    return (
      <a
        href={att.url}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(chip, 'hover:bg-foreground/[0.08]')}
      >
        {inner}
      </a>
    )
  }
  return <div className={chip}>{inner}</div>
}

interface UserMessageProps {
  message: ChatMessage
  projectId: string
  /** Set when the viewer authored the message — enables soft delete. */
  onDelete?: (messageId: string) => void
}

/** Text selected in a reply and sent along with the message ("Add to chat"). */
function QuotedSelections({ quotes }: { quotes: string[] }) {
  const { t } = useTranslation('chat')
  return (
    <div data-slot="quoted-selections" className="flex w-full flex-col gap-1.5">
      {quotes.map((quote, index) => (
        <blockquote
          key={index}
          className="border-border/70 bg-foreground/[0.04] text-foreground/75 rounded-xl border-s-2 px-3.5 py-2 text-[13px] leading-relaxed whitespace-pre-wrap dark:bg-foreground/[0.06]"
        >
          <span className="text-foreground/40 mb-0.5 flex items-center gap-1 text-[11px]">
            <MessageSquareQuote className="size-3" aria-hidden />
            {t('message.quotedSelection')}
          </span>
          <span className="line-clamp-6">{quote}</span>
        </blockquote>
      ))}
    </div>
  )
}

function UserMessageImpl({ message, projectId, onDelete }: UserMessageProps) {
  const { t } = useTranslation('chat')
  const [confirm, setConfirm] = useState(false)
  const deleted = !!message.deletedAt
  const canDelete = !!onDelete && !deleted
  const { quotes, text } = splitQuotedSelections(message.content)

  return (
    <div data-slot="user-message" className="group flex justify-end gap-3">
      <div className="flex max-w-[80%] flex-col items-end gap-1">
        <div className="text-muted-foreground flex items-center gap-2 text-xs">
          {canDelete && (
            <button
              type="button"
              aria-label={t('message.deleteAria')}
              onClick={() => setConfirm(true)}
              className={cn(
                ghostButton,
                'size-6 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100'
              )}
            >
              <Trash2 className="size-3.5" />
            </button>
          )}
          {message.userName && (
            <span className="text-foreground/80 font-medium">{message.userName}</span>
          )}
          {message.createdAt && (
            <span className={cn(mono, 'text-foreground/40 tabular-nums')}>
              {formatTime(message.createdAt)}
            </span>
          )}
        </div>
        {deleted ? (
          <div className="border-border rounded-2xl border border-dashed px-4 py-2.5">
            <p className="text-muted-foreground text-sm italic">{t('message.deletedByUser')}</p>
          </div>
        ) : (
          <>
            {quotes.length > 0 && <QuotedSelections quotes={quotes} />}
            {message.email && <EmailEnvelope email={message.email} projectId={projectId} />}
            {text && (
              <div className="rounded-2xl bg-accent-tint px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap text-accent-tint-foreground">
                {text}
              </div>
            )}
            {message.attachments && message.attachments.length > 0 && (
              <div className="flex flex-wrap justify-end gap-1.5">
                {uniqueAttachments(message.attachments).map((att) => (
                  <AttachmentChip key={att.id} att={att} projectId={projectId} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <Avatar className="size-8 shrink-0">
        {message.userAvatarUrl && (
          <AvatarImage
            src={message.userAvatarUrl}
            alt={message.userName ?? t('message.userFallback')}
          />
        )}
        <AvatarFallback>{initials(message.userName)}</AvatarFallback>
      </Avatar>

      {canDelete && (
        <Dialog open={confirm} onOpenChange={setConfirm}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('message.deleteTitle')}</DialogTitle>
              <DialogDescription>{t('message.deleteDescription')}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirm(false)}>
                {t('message.cancel')}
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  setConfirm(false)
                  onDelete?.(message.id)
                }}
              >
                {t('message.delete')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

export const UserMessage = memo(UserMessageImpl)
